import { useAppStore } from '../store';
import Btn from '../ui/Btn';
import Panel from '../ui/Panel';
import Chip from '../ui/Chip';
import SectionTitle from '../ui/SectionTitle';
import { FC_STAGES, FC_RULES, FC_GROUPS, FC_FEATURED, FC_PARTICIPANTS, FC_SPONSORS, FC_FIXTURES } from './data';
import useFifaTournament from './useFifaTournament';
import { BallIcon, TrophyIcon, Ic, IC, Rv, FCTicker, FutCard, PitchLines, LiveDot } from './ui';

/* сыгранные матчи из расписания: [{ g, a, b, s }] — свежие в конце */
const playedMatches = FC_FIXTURES.flatMap(f => f.matches.filter(m => m.s).map(m => ({ g: f.g, ...m })));

/* ---------- hero ---------- */
function FCHero({ go, T }) {
  const liveGroups = FC_GROUPS.filter(g => g.live);
  return (
    <section style={{ padding: '64px 0 56px' }}>
      <div className="container-wide fc-hero">
        {/* левая колонка */}
        <div className="col" style={{ gap: 22 }}>
          <Rv>
            <div className="row gap-2 wrap">
              <Chip color="var(--accent)" icon={<Ic d={IC.gamepad} size={13} />}>{T.platform}</Chip>
              <Chip color="var(--gold)" icon={<BallIcon size={12} />}>{T.game}</Chip>
              <Chip>{T.participants} УЧАСТНИКА · {T.groupsCount} ГРУПП</Chip>
            </div>
          </Rv>
          <Rv delay={70}>
            <h1 className="display" style={{ margin: 0, fontSize: 'clamp(52px, 7.5vw, 104px)' }}>
              <span className="grad-text">FIFA OPEN</span><br />
              <span className="grad-red">СКО · {T.season}</span>
            </h1>
          </Rv>
          <Rv delay={140}>
            <p style={{ margin: 0, maxWidth: 560, fontSize: 17, lineHeight: 1.6, color: 'var(--fg-soft)' }}>
              Областной футбольный киберспортивный чемпионат в формате чемпионата мира:
              8 групп, затем плей-офф с 1/8 финала. Сборные раздаёт жеребьёвка в день турнира —
              всё проходит в спортбаре SALEM.
            </p>
          </Rv>
          <Rv delay={210}>
            <div className="row gap-3 wrap" style={{ marginTop: 6 }}>
              <Btn size="lg" icon={<LiveDot />} onClick={() => go('stream')}>Смотреть эфир</Btn>
              <Btn size="lg" variant="ghost" icon={<TrophyIcon size={16} />} onClick={() => go('bracket')}>Турнирная сетка</Btn>
            </div>
          </Rv>
          <Rv delay={245}>
            <a href={`tel:${T.regPhone.replace(/\s/g, '')}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', maxWidth: 560 }}
              title="Позвонить и зарегистрироваться">
              <div className="row gap-4 wrap" style={{
                position: 'relative', overflow: 'hidden', padding: '16px 20px', borderRadius: 'var(--r-lg)',
                background: 'linear-gradient(120deg, rgba(225,18,23,0.18), rgba(230,183,60,0.10) 55%, var(--surface))',
                border: '1px solid rgba(230,183,60,0.5)',
                boxShadow: 'var(--shadow-card), var(--glow-gold)',
              }}>
                <span className="center" style={{
                  width: 52, height: 52, borderRadius: '50%', flexShrink: 0, color: '#fff',
                  background: 'linear-gradient(140deg, var(--accent-bright), var(--accent-deep))',
                  boxShadow: '0 10px 26px -8px var(--accent-glow)',
                }}>
                  <Ic d={IC.phone} size={22} />
                </span>
                <div className="col gap-1" style={{ minWidth: 0 }}>
                  <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--gold)', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--gold)', boxShadow: '0 0 8px rgba(230,183,60,0.7)' }} />
                    Регистрация на турнир — по телефону
                  </span>
                  <span className="display" style={{ fontSize: 'clamp(22px, 4.5vw, 30px)', color: 'var(--fg)', whiteSpace: 'nowrap' }}>{T.regPhone}</span>
                  <span style={{ fontSize: 13.5, color: 'var(--fg-soft)' }}>
                    Взнос — <b style={{ color: 'var(--gold)' }}>{T.fee}</b> · звоните или пишите, запишем вашу заявку
                  </span>
                </div>
              </div>
            </a>
          </Rv>
          <Rv delay={280}>
            <div className="row gap-5 wrap" style={{ marginTop: 10 }}>
              {[[IC.cal, T.date], [IC.pin, T.place], [IC.users, T.eligibility]].map(([d, l], i) => (
                <span key={i} className="row gap-2" style={{ fontSize: 13.5, color: 'var(--fg-muted)' }}>
                  <Ic d={d} size={15} color="var(--accent-bright)" />{l}
                </span>
              ))}
            </div>
          </Rv>
        </div>

        {/* правая колонка — live-матчи или предстартовая сводка */}
        <Rv delay={160}>
          <Panel pad={24} glow style={{ overflow: 'hidden' }}>
            <PitchLines opacity="calc(var(--pitch-a) * 0.4)" />
            {liveGroups.length ? (
              <div className="col gap-4" style={{ position: 'relative' }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LiveDot />Сейчас играют
                  </span>
                  <Chip color="var(--accent)">ГРУППОВОЙ ЭТАП</Chip>
                </div>
                <div className="col gap-2">
                  {liveGroups.map(g => (
                    <div className="fc-lm-row" key={g.id}>
                      <span className="fc-lm-g">{g.id}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {g.live.a} <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>vs</span> {g.live.b}
                      </span>
                      <span className="row gap-2" style={{ alignItems: 'center' }}>
                        <span className="fc-lm-score">{g.live.ga}:{g.live.gb}</span>
                        <span className="mono" style={{ fontSize: 10, color: 'var(--accent-bright)' }}>{g.live.min}′</span>
                      </span>
                    </div>
                  ))}
                </div>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                  <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Все матчи — в прямом эфире</span>
                  <Btn size="sm" variant="outline" onClick={() => go('stream')}>В эфир →</Btn>
                </div>
              </div>
            ) : playedMatches.length ? (
              <div className="col gap-4" style={{ position: 'relative' }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LiveDot />Турнир идёт
                  </span>
                  <Chip color="var(--accent)">РЕЗУЛЬТАТЫ</Chip>
                </div>
                <div className="col gap-2">
                  {playedMatches.slice(-5).reverse().map((m, i) => (
                    <div className="fc-lm-row" key={i}>
                      <span className="fc-lm-g">{m.g}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.a} <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>vs</span> {m.b}
                      </span>
                      <span className="fc-lm-score">{m.s}</span>
                    </div>
                  ))}
                </div>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                  <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Таблицы и расписание — на странице сетки</span>
                  <Btn size="sm" variant="outline" onClick={() => go('bracket')}>Сетка →</Btn>
                </div>
              </div>
            ) : (
              <div className="col gap-4" style={{ position: 'relative' }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LiveDot />Скоро старт
                  </span>
                  <Chip color="var(--gold)">12 ИЮЛЯ · SALEM</Chip>
                </div>
                <div className="col center gap-1" style={{ padding: '10px 0 4px' }}>
                  <span className="display grad-text" style={{ fontSize: 'clamp(34px, 4.5vw, 46px)' }}>12 ИЮЛЯ</span>
                  <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.2em', color: 'var(--fg-muted)', textTransform: 'uppercase' }}>
                    Спортбар SALEM · Петропавловск
                  </span>
                </div>
                <div className="col gap-2">
                  {[
                    [`${T.participants} участника`, 'полный список — на странице сетки'],
                    [`${T.groupsCount} групп`, 'по два лучших — в плей-офф'],
                    ['Сборные — по жеребьёвке', 'у каждого своя команда'],
                  ].map(([k, v]) => (
                    <div className="fc-lm-row" key={k} style={{ gridTemplateColumns: '1fr auto' }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600 }}>{k}</span>
                      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)', letterSpacing: '0.06em' }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                  <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Группы и сетка уже опубликованы</span>
                  <Btn size="sm" variant="outline" onClick={() => go('bracket')}>Смотреть →</Btn>
                </div>
              </div>
            )}
          </Panel>
        </Rv>
      </div>
    </section>
  );
}

/* ---------- инфо-карточки ---------- */
function FCInfoCards({ go, T }) {
  const cards = [
    { icon: <Ic d={IC.grid} size={20} />, t: 'Формат', v: `${T.groupsCount} групп → плей-офф`, d: 'Как на чемпионате мира: группы, потом 1/8 финала и навылет' },
    { icon: <Ic d={IC.users} size={20} />, t: 'Участники', v: `${T.participants} игрока`, d: '1 на 1 · каждый играет за сборную из жеребьёвки' },
    { icon: <TrophyIcon size={20} />, t: 'Призы', v: T.prize.split('·')[0].trim(), d: T.prize },
    { icon: <Ic d={IC.tv} size={20} />, t: 'Эфир', v: 'Twitch + YouTube', d: 'Все туры в прямом эфире, записи — на канале', go: 'stream' },
  ];
  return (
    <section style={{ padding: '34px 0 10px' }}>
      <div className="container-wide fc-cards">
        {cards.map((c, i) => (
          <Rv key={c.t} delay={i * 70}>
            <Panel pad={20} style={{ height: '100%', cursor: c.go ? 'pointer' : 'default' }}>
              <div onClick={c.go ? () => go(c.go) : undefined} className="col gap-2">
                <div className="row gap-3" style={{ alignItems: 'center' }}>
                  <span className="center" style={{ width: 40, height: 40, borderRadius: 'var(--r-md)', color: 'var(--accent-bright)',
                    background: 'rgba(225,18,23,0.08)', border: '1px solid var(--line-accent)' }}>{c.icon}</span>
                  <span className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--fg-muted)' }}>{c.t}</span>
                </div>
                <div className="display" style={{ fontSize: 21, marginTop: 6 }}>{c.v}</div>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{c.d}</div>
              </div>
            </Panel>
          </Rv>
        ))}
      </div>
    </section>
  );
}

/* ---------- этапы с прогрессом ---------- */
function FCStages() {
  return (
    <section style={{ padding: '34px 0' }}>
      <div className="container-wide">
        <SectionTitle eyebrow="Путь к кубку" title="Как проходит чемпионат"
          sub="Групповые туры играются по 4 матча одновременно на столах 1–4 — весь тур занимает один вечер." />
        <div className="fc-steps">
          {FC_STAGES.map((s, i) => (
            <Rv key={s.n} delay={i * 80}>
              <Panel pad={20} className={`fc-step-card ${s.state}`} style={{ height: '100%' }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span className="fc-step-num">{s.n}</span>
                  {s.state === 'done' && <Chip color="var(--ok)">✓</Chip>}
                  {s.state === 'now' && <Chip color="var(--accent)" icon={<LiveDot size={6} />}>СЕЙЧАС</Chip>}
                  {s.state === 'next' && i === FC_STAGES.length - 1 && <TrophyIcon size={18} color="var(--gold)" />}
                </div>
                <div className="display" style={{ fontSize: 19, margin: '10px 0 6px' }}>{s.t}</div>
                <div style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.55 }}>{s.d}</div>
              </Panel>
            </Rv>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FUT-фавориты · скрыты, пока нет результатов ---------- */
function FCFavorites({ go }) {
  if (!FC_FEATURED.length) return null;
  return (
    <section style={{ padding: '34px 0' }}>
      <div className="container-wide" style={{ position: 'relative' }}>
        <SectionTitle eyebrow="Карточки игроков" title="Фавориты турнира"
          sub="Рейтинг составлен по итогам жеребьёвки и групповых матчей. Форма — три последних игры."
          action={<Btn variant="outline" size="sm" onClick={() => go('bracket')}>Все группы →</Btn>} />
        <Rv>
          <div className="fut-row">
            {FC_FEATURED.map((p, i) => <FutCard key={p.n} p={p} delay={i * 60} />)}
          </div>
        </Rv>
      </div>
    </section>
  );
}

/* ---------- правила + эфир ---------- */
function FCRules({ go, T }) {
  return (
    <section style={{ padding: '34px 0 56px' }}>
      <div className="container-wide">
        <SectionTitle eyebrow="Регламент" title="Правила — коротко" sub="Полный регламент — в Telegram-канале турнира." />
        <div className="fc-two">
          <Rv>
            <Panel pad={26} accent style={{ height: '100%' }}>
              {FC_RULES.map((r, i) => (
                <div key={i} className="fc-rule-li">
                  <BallIcon size={15} color="var(--gold)" style={{ flexShrink: 0, marginTop: 3 }} />
                  <span>{r}</span>
                </div>
              ))}
            </Panel>
          </Rv>
          <div className="col gap-4">
            <Rv delay={80}>
              <Panel pad={24} style={{ position: 'relative', overflow: 'hidden' }}>
                <PitchLines opacity="calc(var(--pitch-a) * 0.35)" />
                <div className="col gap-4" style={{ position: 'relative' }}>
                  <span className="eyebrow">Место и организаторы</span>
                  {[[IC.phone, 'Регистрация по телефону', `${T.regPhone} · взнос ${T.fee}`],
                    [IC.pin, 'Место проведения', `${T.partner} · Петропавловск`],
                    [IC.cal, 'Весь турнир — за один день', T.date],
                    [IC.users, 'Организатор', T.organizer],
                    [IC.send, 'Связь', `Telegram ${T.telegram}`]].map(([d, k, v]) => (
                    <div key={k} className="row gap-3" style={{ alignItems: 'flex-start' }}>
                      <span className="center" style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 'var(--r-sm)', color: 'var(--accent-bright)', background: 'rgba(225,18,23,0.08)', border: '1px solid var(--line-accent)' }}>
                        <Ic d={d} size={15} />
                      </span>
                      <div className="col">
                        <span style={{ fontSize: 14.5, fontWeight: 600 }}>{k}</span>
                        <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 2, lineHeight: 1.5 }}>{v}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            </Rv>
            <Rv delay={160}>
              <Panel pad={24} glow style={{ background: 'linear-gradient(140deg, rgba(225,18,23,0.14), var(--surface) 55%)' }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div className="col gap-1">
                    <span className="display" style={{ fontSize: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <LiveDot />Прямой эфир
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>Twitch и YouTube · 4 стола, студия, обзоры</span>
                  </div>
                  <Btn onClick={() => go('stream')} icon={<Ic d={IC.play} size={15} />}>Смотреть</Btn>
                </div>
              </Panel>
            </Rv>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- организаторы и спонсоры ---------- */
function FCSponsors() {
  return (
    <section style={{ padding: '10px 0 56px' }}>
      <div className="container-wide">
        <SectionTitle eyebrow="Кто делает турнир" title="Организаторы и спонсоры" />
        <div className="fc-sponsors">
          {FC_SPONSORS.map((s, i) => (
            <Rv key={s.name} delay={i * 80}>
              <Panel pad={22} style={{ height: '100%' }}>
                <div className="row gap-4" style={{ alignItems: 'center' }}>
                  <div style={{
                    width: 68, height: 68, borderRadius: '50%', flexShrink: 0,
                    backgroundImage: `url(${s.logo})`, backgroundSize: 'cover', backgroundPosition: 'center',
                    backgroundColor: s.light ? '#fff' : 'transparent',
                    border: '1.5px solid var(--line-strong)',
                    boxShadow: '0 10px 26px -12px rgba(0,0,0,0.8)',
                  }} />
                  <div className="col" style={{ minWidth: 0 }}>
                    <div className="display" style={{ fontSize: 19 }}>{s.name}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.5 }}>{s.sub}</div>
                  </div>
                </div>
              </Panel>
            </Rv>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- страница ---------- */
export default function FifaHomePage() {
  const { go } = useAppStore();
  const T = useFifaTournament();
  const tickerItems = playedMatches.length
    ? playedMatches.map(m => ({ b: m.g, t: `${m.a} ${m.s} ${m.b}` }))
    : FC_PARTICIPANTS.map(p => ({ t: `${p.name} — ${p.team}` }));
  return (
    <main className="page-in">
      <FCHero go={go} T={T} />
      <FCTicker tag={playedMatches.length ? 'РЕЗУЛЬТАТЫ' : 'УЧАСТНИКИ'} items={tickerItems} />
      <FCInfoCards go={go} T={T} />
      <FCStages />
      <FCFavorites go={go} />
      <FCRules go={go} T={T} />
      <FCSponsors />
    </main>
  );
}
